import React from 'react';
import {
  Box, Paper, Typography, IconButton, RadioGroup, Radio, FormControlLabel,
  ToggleButtonGroup, ToggleButton, Tooltip, Switch, FormGroup,
} from '@mui/material';
import {
  ArrowBack as PrevIcon,
  ArrowForward as NextIcon,
  Refresh as RefreshIcon,
  ViewColumn as ColumnsIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { DreRegime } from '../../services/dreService';

export interface DreFiltersProps {
  year: number;
  onYearChange: (year: number) => void;
  regime: DreRegime;
  onRegimeChange: (regime: DreRegime) => void;
  showAv: boolean;
  showAh: boolean;
  showYearTotal: boolean;
  onToggleAv: () => void;
  onToggleAh: () => void;
  onToggleYearTotal: () => void;
  onRefresh: () => void;
  onOpenSettings?: () => void;
  units?: { id: string; name: string }[];
  selectedUnitIds?: string[];
  onUnitsChange?: (ids: string[]) => void;
  loading?: boolean;
}

export const DreFilters: React.FC<DreFiltersProps> = ({
  year, onYearChange, regime, onRegimeChange,
  showAv, showAh, showYearTotal,
  onToggleAv, onToggleAh, onToggleYearTotal,
  onRefresh, onOpenSettings,
  units = [], selectedUnitIds = [], onUnitsChange,
  loading,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1, md: 1.5 },
        borderRadius: 2,
        border: '1px solid #e2e8f0',
        bgcolor: '#fff',
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2,
      }}
    >
      {/* Regime */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>
          Visualizar
        </Typography>
        <RadioGroup
          row
          value={regime}
          onChange={(e) => onRegimeChange(e.target.value as DreRegime)}
          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.78rem', fontWeight: 600 } }}
        >
          <FormControlLabel value="cash" control={<Radio size="small" />} label="Apenas realizado" />
          <FormControlLabel value="accrual" control={<Radio size="small" />} label="Apenas esperado" />
          <FormControlLabel value="hybrid" control={<Radio size="small" />} label="Esperado e realizado" />
        </RadioGroup>
      </Box>

      <Box sx={{ flex: 1 }} />

      {/* Período */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>
          Período
        </Typography>
        <Box sx={{
          display: 'flex', alignItems: 'center',
          border: '1px solid #cbd5e1', borderRadius: 1.5,
          overflow: 'hidden', bgcolor: '#fff',
        }}>
          <IconButton size="small" onClick={() => onYearChange(year - 1)} sx={{ borderRadius: 0 }}>
            <PrevIcon fontSize="small" />
          </IconButton>
          <Typography sx={{
            px: 2.5, py: 0.5, fontSize: '0.95rem', fontWeight: 700, color: '#0f172a',
            borderLeft: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0',
            minWidth: 70, textAlign: 'center',
          }}>
            {year}
          </Typography>
          <IconButton size="small" onClick={() => onYearChange(year + 1)} sx={{ borderRadius: 0 }}>
            <NextIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Unidades */}
      {units.length > 0 && onUnitsChange && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>
            Unidades
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            <ToggleButton
              value="__all_units__"
              selected={selectedUnitIds.length === 0}
              onClick={() => onUnitsChange([])}
              size="small"
              sx={{
                textTransform: 'none', fontSize: '0.7rem', fontWeight: 600,
                py: 0.3, px: 1.2, borderRadius: 1,
                '&.Mui-selected': { bgcolor: '#dbeafe !important', color: '#1e40af !important' },
              }}
            >
              Todas
            </ToggleButton>
            <ToggleButtonGroup
              exclusive={false}
              value={selectedUnitIds}
              size="small"
              onChange={(_, val) => {
                // Com exclusive=false o valor é string[]; com exclusive inadvertido pode vir string|null
                if (val == null) {
                  onUnitsChange([]);
                  return;
                }
                if (typeof val === 'string') {
                  onUnitsChange(val === '' ? [] : [val]);
                  return;
                }
                onUnitsChange(Array.isArray(val) ? val : []);
              }}
              sx={{
                flexWrap: 'wrap',
                '& .MuiToggleButton-root': {
                  textTransform: 'none', fontSize: '0.7rem', fontWeight: 600,
                  py: 0.3, px: 1.2, borderRadius: 1,
                },
                '& .Mui-selected': { bgcolor: '#dbeafe !important', color: '#1e40af !important' },
              }}
            >
              {units.map((u) => (
                <ToggleButton key={u.id} value={u.id}>{u.name}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Box>
      )}

      {/* Ações */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title="Atualizar dados">
          <span>
            <IconButton size="small" onClick={onRefresh} disabled={loading}>
              <RefreshIcon fontSize="small" sx={loading ? { animation: 'spin 1s linear infinite' } : {}} />
            </IconButton>
          </span>
        </Tooltip>

        {onOpenSettings && (
          <Tooltip title="Configurações do DRE">
            <IconButton size="small" onClick={onOpenSettings}>
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Toggles de coluna */}
      <FormGroup row sx={{ ml: 'auto', gap: 1 }}>
        <FormControlLabel
          control={<Switch size="small" checked={showAv} onChange={onToggleAv} />}
          label={<Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b' }}>AV</Typography>}
          sx={{ m: 0 }}
        />
        <FormControlLabel
          control={<Switch size="small" checked={showAh} onChange={onToggleAh} />}
          label={<Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b' }}>AH</Typography>}
          sx={{ m: 0 }}
        />
        <FormControlLabel
          control={<Switch size="small" checked={showYearTotal} onChange={onToggleYearTotal} />}
          label={<Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b' }}>Total ano</Typography>}
          sx={{ m: 0 }}
        />
      </FormGroup>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </Paper>
  );
};

export default DreFilters;
